import type { TableColumn as TableColumnWSR } from '@wix/design-system';
import type { KeyedItem, SortDirection } from '@wix/bex-core';
import { CSSProperties, ReactNode } from 'react';

type TableColumnBase<T = any> = Omit<
  TableColumnWSR<T>,
  'sortDescending' | 'sortable' | 'infoTooltipProps' | 'style'
> & {
  style?:
    | CSSProperties
    | ((
        column: TableColumnWSR,
        rowData: KeyedItem<T>,
        rowNum: number,
      ) => CSSProperties);
  reorderDisabled?: boolean;
  defaultSortDirection?: SortDirection;
  sortMode?: 'always';
  infoTooltipProps?: TableColumnWSR['infoTooltipProps'] & {
    panelContent?: ReactNode;
  };
};

// disallow specifying `hideable` / `defaultHidden` or setting `hiddenFromCustomColumnsSelection` as false when `id` is missing
interface TableColumnOptional<T> extends TableColumnBase<T> {
  id?: string;
  name?: string;
  hiddenFromCustomColumnsSelection?: undefined | true;
  hideable?: undefined;
  defaultHidden?: undefined;
}

// common case for custom column definition
interface TableColumnCustom<T> extends TableColumnBase<T> {
  id: string;
  name: string;
  hiddenFromCustomColumnsSelection?: false;
  hideable?: true;
  defaultHidden?: boolean;
}

// allow `name` to be optional when `title` exists
interface TableColumnCustomOptionalName<T> extends TableColumnBase<T> {
  id: string;
  name?: string;
  title: TableColumnBase['title'];
  hiddenFromCustomColumnsSelection?: false;
  hideable?: true;
  defaultHidden?: boolean;
}

// force `defaultHidden=false|undefined` when `hideable=false`
interface TableColumnCustomDefaultHidden<T> extends TableColumnBase<T> {
  id: string;
  name?: string;
  title: TableColumnBase['title'];
  hiddenFromCustomColumnsSelection?: false;
  hideable: false;
  defaultHidden?: undefined | false;
}

export type CustomColumnConfig<T> =
  | TableColumnOptional<T>
  | TableColumnCustom<T>
  | TableColumnCustomDefaultHidden<T>
  | TableColumnCustomOptionalName<T>;

export interface SortableColumnConfig<T> extends TableColumnBase<T> {
  id: string;
  sortable: true;
}

export interface NotSortableColumnConfig<T> extends TableColumnBase<T> {
  id?: string;
  sortable?: false;
}

export type SortableConfig<T> =
  | SortableColumnConfig<T>
  | NotSortableColumnConfig<T>;

export type TableColumn<T> = CustomColumnConfig<T> & SortableConfig<T>;

export interface Column {
  id: string;
  name: string;
  title: TableColumnBase['title'];
  hiddenFromCustomColumnsSelection?: boolean;
  hideable?: boolean;
  reorderDisabled?: boolean;
  sortable?: boolean;
  sortDescending?: boolean;
  sortMode?: 'always';
  defaultSortDirection?: SortDirection;
  defaultHidden?: boolean;
  dataExtension?: { id: string; editable: boolean };
  infoTooltipProps?: {
    content?: ReactNode;
    panelContent?: ReactNode;
  };
  render: TableColumnWSR['render'];
  width?: TableColumnWSR['width'];
  style?: TableColumnWSR['style'];
}
